import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

const empty = () => ({ code: '', name: '', netDays: '30', discountDays: '10', discountPercent: '2' });

export function PaymentTermModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setForm(empty()); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: (body) => financeApi.createPaymentTerm(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['finance', 'payment-terms'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.code.trim() || !form.name.trim() || form.netDays === '') { setError(t('pt.error.required')); return; }
    save.mutate({ code: form.code.trim(), name: form.name.trim(), netDays: Number(form.netDays), discountDays: Number(form.discountDays) || 0, discountPercent: Number(form.discountPercent) || 0 });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('pt.modal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <div className="field-row">
          <label className="field" style={{ maxWidth: 160 }}><span className="field-label">{t('pt.col.code')}</span>
            <input className="input mono" value={form.code} onChange={set('code')} placeholder="2/10 N30" required />
          </label>
          <label className="field"><span className="field-label">{t('pt.col.name')}</span>
            <input className="input" value={form.name} onChange={set('name')} required />
          </label>
        </div>
        <div className="field-row">
          <label className="field" style={{ maxWidth: 140 }}><span className="field-label">{t('pt.field.netDays')}</span>
            <input className="input num" type="number" min="0" value={form.netDays} onChange={set('netDays')} required />
          </label>
          <label className="field" style={{ maxWidth: 140 }}><span className="field-label">{t('pt.field.discountDays')}</span>
            <input className="input num" type="number" min="0" value={form.discountDays} onChange={set('discountDays')} />
          </label>
          <label className="field" style={{ maxWidth: 160 }}><span className="field-label">{t('pt.field.discountPercent')}</span>
            <input className="input num" type="number" min="0" max="100" step="0.01" value={form.discountPercent} onChange={set('discountPercent')} />
          </label>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
